import React from 'react';
import { useAdminFetch } from '@/lib/adminApi';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ShareIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

interface ActivitySummary {
  totalUploads: number;
  totalDownloads: number;
  totalShares: number;
  activeUsers: number;
  topDownloadedFiles: Array<{ fileId: string; fileName: string; downloads: number }>;
  recentLogs: Array<{
    id: string;
    type: string;
    description: string;
    userName: string;
    createdAt: string;
  }>;
  counted?: Record<string, string>;
}

const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ComponentType<any>;
  colorClass: string;
}> = ({ title, value, icon: Icon, colorClass }) => (
  <div className="bg-card overflow-hidden shadow rounded-lg">
    <div className="p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <Icon className={`h-6 w-6 ${colorClass}`} aria-hidden="true" />
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-muted-foreground truncate">{title}</dt>
            <dd className="text-lg font-medium text-foreground">{value}</dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
);

export function AdminDashboard() {
  const [summary, setSummary] = React.useState<ActivitySummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const adminGet = useAdminFetch();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith('tr') ? tr : enUS;

  React.useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await adminGet<ActivitySummary>('/admin/summary');
        setSummary(data);
      } catch (error) {
        console.error('Error fetching summary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="text-center text-muted-foreground">{t('admin.loadFailed')}</div>
    );
  }

  return (
    <div className="space-y-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title={t('admin.dashboard.totalUploads')}
            value={summary.totalUploads}
            icon={ArrowUpIcon}
            colorClass="text-success"
          />
          <StatCard
            title={t('admin.dashboard.totalDownloads')}
            value={summary.totalDownloads}
            icon={ArrowDownIcon}
            colorClass="text-accent"
          />
          <StatCard
            title={t('admin.dashboard.totalShares')}
            value={summary.totalShares}
            icon={ShareIcon}
            colorClass="text-purple-500"
          />
          <StatCard
            title={t('admin.dashboard.activeUsers')}
            value={summary.activeUsers}
            icon={UsersIcon}
            colorClass="text-orange-500"
          />
        </div>

        <div className="bg-card shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-foreground">
              {t('admin.dashboard.topDownloaded')}
            </h3>
          </div>
          <div className="border-t border-border-color">
            <ul role="list" className="divide-y divide-border-color">
              {summary.topDownloadedFiles.map((file) => (
                <li key={file.fileId} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-foreground">{file.fileName}</div>
                    <div className="text-sm text-muted-foreground">{t('admin.dashboard.downloads', { count: file.downloads })}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="bg-card shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-foreground">
              {t('admin.dashboard.recentActivity')}
            </h3>
          </div>
          <div className="border-t border-border-color">
            <ul role="list" className="divide-y divide-border-color">
              {summary.recentLogs.map((activity) => (
                <li key={activity.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-foreground">{activity.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.userName} -{' '}
                        {format(new Date(activity.createdAt), 'dd MMMM yyyy HH:mm', {
                          locale: dateLocale,
                        })}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground">{activity.type}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
    </div>
  );
}
